import { apiClient } from './apiClient';
import type {
  AdjacentCrop,
  AdjacentCropDetail,
  AdjacentCropGeoJSON,
  AdjacentCropFeatureCollection,
  AdjacentCropReview,
  AdjacentCropStatistics,
  AdjacentCropDashboardData,
  AdjacentCropFromAuditFormValues,
  AdjacentCropReviewFormValues,
  AdjacentCropCloseFormValues,
  AdjacentCropFilters,
  OpenAdjacentCropsResponse,
  CropSpecies,
  CropVariety,
  SpeciesVarietiesResponse,
} from '../../types/adjacent-crop';

/**
 * Error response structure from API
 */
interface ApiError {
  message: string;
  status?: number;
  originalError?: any;
}

/**
 * Paginated response structure
 */
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Adjacent Crop API Service
 * Handles all adjacent crop-related API operations
 */
export class AdjacentCropService {
  /**
   * Get all adjacent crops with optional filters
   * 
   * @param filters - Optional filters to apply
   * @returns Promise with array of adjacent crops
   * 
   * @example
   * ```typescript
   * const adjacentCrops = await adjacentCropService.getAdjacentCrops({
   *   status: 'open',
   *   farm: 123
   * });
   * ```
   */
  async getAdjacentCrops(filters?: AdjacentCropFilters): Promise<AdjacentCrop[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/adjacent-crops/?${queryString}` : '/adjacent-crops/';
      
      const response = await apiClient.get<any>(url);
      
      // Handle GeoJSON FeatureCollection format
      if (response.data && response.data.type === 'FeatureCollection') {
        // Extract properties from GeoJSON features
        return response.data.features.map((feature: AdjacentCropGeoJSON) => ({
          ...feature.properties,
          id: feature.id || feature.properties.id,
          geom: feature.geometry,
        }));
      }
      
      // Handle paginated response
      if (response.data && response.data.results) {
        return response.data.results;
      }
      
      // Handle direct array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.fetch_failed');
    }
  }

  /**
   * Get a single adjacent crop by ID with full details
   * 
   * @param id - The ID of the adjacent crop
   * @returns Promise with the adjacent crop detail
   * 
   * @example
   * ```typescript
   * const adjacentCrop = await adjacentCropService.getAdjacentCrop('uuid-123');
   * ```
   */
  async getAdjacentCrop(id: string): Promise<AdjacentCropDetail> {
    try {
      const response = await apiClient.get<AdjacentCropDetail>(
        `/adjacent-crops/${id}/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.fetch_failed');
    }
  }

  /**
   * Create a new adjacent crop (admin only)
   * 
   * @param data - The adjacent crop data to create
   * @returns Promise with the created adjacent crop
   * 
   * @example
   * ```typescript
   * const newCrop = await adjacentCropService.createAdjacentCrop({
   *   species: 'uuid-species',
   *   variety: 'uuid-variety',
   *   farm: 123,
   *   is_producer_owner: true,
   *   geom: { type: 'Point', coordinates: [-70.6693, -33.4489] }
   * });
   * ```
   */
  async createAdjacentCrop(data: Partial<AdjacentCrop>): Promise<AdjacentCrop> {
    try {
      const response = await apiClient.post<AdjacentCrop>(
        '/adjacent-crops/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.create_failed');
    }
  }

  /**
   * Update an existing adjacent crop
   * 
   * @param id - The ID of the adjacent crop to update
   * @param data - The partial adjacent crop data to update
   * @returns Promise with the updated adjacent crop
   * 
   * @example
   * ```typescript
   * const updatedCrop = await adjacentCropService.updateAdjacentCrop('uuid-123', {
   *   variety: 'uuid-new-variety',
   *   is_producer_owner: false
   * });
   * ```
   */
  async updateAdjacentCrop(
    id: string,
    data: Partial<AdjacentCrop>
  ): Promise<AdjacentCrop> {
    try {
      const response = await apiClient.patch<AdjacentCrop>(
        `/adjacent-crops/${id}/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.update_failed');
    }
  }

  /**
   * Delete an adjacent crop
   * 
   * @param id - The ID of the adjacent crop to delete
   * @returns Promise that resolves when deletion is complete
   * 
   * @example
   * ```typescript
   * await adjacentCropService.deleteAdjacentCrop('uuid-123');
   * ```
   */
  async deleteAdjacentCrop(id: string): Promise<void> {
    try {
      await apiClient.delete(`/adjacent-crops/${id}/`);
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.delete_failed');
    }
  }

  /**
   * Close an adjacent crop
   * 
   * @param id - The ID of the adjacent crop to close
   * @param data - The closure data
   * @returns Promise with the updated adjacent crop
   * 
   * @example
   * ```typescript
   * const closedCrop = await adjacentCropService.closeAdjacentCrop('uuid-123', {
   *   closed_date: '2024-01-15',
   *   comment_closed: 'Crop disappeared'
   * });
   * ```
   */
  async closeAdjacentCrop(
    id: string,
    data: AdjacentCropCloseFormValues
  ): Promise<AdjacentCropDetail> {
    try {
      const response = await apiClient.post<AdjacentCropDetail>(
        `/adjacent-crops/${id}/close/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.close_failed');
    }
  }

  /**
   * Get statistics for adjacent crops dashboard
   * 
   * @returns Promise with adjacent crop statistics
   * 
   * @example
   * ```typescript
   * const stats = await adjacentCropService.getStatistics();
   * console.log(`Total adjacent crops: ${stats.total_adjacent_crops}`);
   * ```
   */
  async getStatistics(): Promise<AdjacentCropStatistics> {
    try {
      const response = await apiClient.get<AdjacentCropStatistics>(
        '/adjacent-crops/statistics/'
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.statistics_failed');
    }
  }

  /**
   * Get comprehensive dashboard data with optional filters
   * 
   * @param filters - Optional filters (date_from, date_to, country_id, producer_id)
   * @returns Promise with dashboard data
   * 
   * @example
   * ```typescript
   * const dashboardData = await adjacentCropService.getDashboardData({
   *   date_from: '2024-01-01',
   *   date_to: '2024-12-31',
   *   country_id: 1
   * });
   * ```
   */
  async getDashboardData(filters?: {
    date_from?: string;
    date_to?: string;
    country_id?: number;
    producer_id?: number;
  }): Promise<AdjacentCropDashboardData> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
      }

      const queryString = params.toString();
      const url = queryString 
        ? `/adjacent-crops/dashboard-data/?${queryString}`
        : '/adjacent-crops/dashboard-data/';
      
      const response = await apiClient.get<AdjacentCropDashboardData>(url);
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.dashboard_data_failed');
    }
  }

  /**
   * Export adjacent crops data to Excel
   * 
   * @param filters - Optional filters to apply to export
   * @returns Promise with blob data for download
   * 
   * @example
   * ```typescript
   * const blob = await adjacentCropService.exportToExcel({
   *   status: 'open',
   *   date_from: '2024-01-01'
   * });
   * // Create download link
   * const url = window.URL.createObjectURL(blob);
   * const link = document.createElement('a');
   * link.href = url;
   * link.download = 'adjacent_crops.xlsx';
   * link.click();
   * ```
   */
  async exportToExcel(filters?: {
    date_from?: string;
    date_to?: string;
    status?: string;
    species_id?: string;
  }): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
      }

      const queryString = params.toString();
      const url = queryString 
        ? `/adjacent-crops/export/?${queryString}`
        : '/adjacent-crops/export/';
      
      const response = await apiClient.get(url, {
        responseType: 'blob',
      });
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.export_failed');
    }
  }

  /**
   * Get all adjacent crop reviews with optional filters
   * 
   * @param filters - Optional filters (adjacent_crop_id, audit_id, is_first_review)
   * @returns Promise with array of reviews
   * 
   * @example
   * ```typescript
   * const reviews = await adjacentCropService.getReviews({
   *   adjacent_crop_id: 'uuid-123'
   * });
   * ```
   */
  async getReviews(filters?: {
    adjacent_crop_id?: string;
    audit_id?: number;
    is_first_review?: boolean;
  }): Promise<AdjacentCropReview[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
      }

      const queryString = params.toString();
      const url = queryString 
        ? `/adjacent-crop-reviews/?${queryString}`
        : '/adjacent-crop-reviews/';
      
      const response = await apiClient.get<any>(url);
      
      // Handle paginated response
      if (response.data && response.data.results) {
        return response.data.results;
      }
      
      // Handle direct array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.reviews_fetch_failed');
    }
  }

  /**
   * Get a single review by ID
   * 
   * @param id - The ID of the review
   * @returns Promise with the review detail
   * 
   * @example
   * ```typescript
   * const review = await adjacentCropService.getReview('uuid-123');
   * ```
   */
  async getReview(id: string): Promise<AdjacentCropReview> {
    try {
      const response = await apiClient.get<AdjacentCropReview>(
        `/adjacent-crop-reviews/${id}/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.review_fetch_failed');
    }
  }

  /**
   * Create a new review (admin only - use createReviewFromAudit for audit context)
   * 
   * @param data - The review data to create
   * @returns Promise with the created review
   */
  async createReview(data: Partial<AdjacentCropReview>): Promise<AdjacentCropReview> {
    try {
      const response = await apiClient.post<AdjacentCropReview>(
        '/adjacent-crop-reviews/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.review_create_failed');
    }
  }

  /**
   * Update an existing review
   * 
   * @param id - The ID of the review to update
   * @param data - The partial review data to update
   * @returns Promise with the updated review
   */
  async updateReview(
    id: string,
    data: Partial<AdjacentCropReview>
  ): Promise<AdjacentCropReview> {
    try {
      const response = await apiClient.patch<AdjacentCropReview>(
        `/adjacent-crop-reviews/${id}/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.review_update_failed');
    }
  }

  /**
   * Delete a review
   * 
   * @param id - The ID of the review to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteReview(id: string): Promise<void> {
    try {
      await apiClient.delete(`/adjacent-crop-reviews/${id}/`);
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.review_delete_failed');
    }
  }

  /**
   * Get all OPEN adjacent crops for a specific farm
   * Used when starting an audit to show which adjacent crops need to be reviewed
   * 
   * @param farmId - The ID of the farm
   * @param auditId - Optional audit ID to filter by date
   * @returns Promise with open adjacent crops response
   * 
   * @example
   * ```typescript
   * const openCrops = await adjacentCropService.getOpenAdjacentCropsForFarm(123, 456);
   * ```
   */
  async getOpenAdjacentCropsForFarm(
    farmId: number,
    auditId?: number
  ): Promise<OpenAdjacentCropsResponse> {
    try {
      const url = auditId 
        ? `/audit-adjacent-crops/open-for-farm/?farm_id=${farmId}&audit_id=${auditId}`
        : `/audit-adjacent-crops/open-for-farm/?farm_id=${farmId}`;
      
      const response = await apiClient.get<OpenAdjacentCropsResponse>(url);
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.fetch_failed');
    }
  }

  /**
   * Create an adjacent crop from an audit context
   * 
   * This endpoint automatically associates the adjacent crop with the audit's farm
   * and creates the first review linked to the audit.
   * 
   * @param data - The adjacent crop data (without farm field)
   * @returns Promise with the created adjacent crop
   * 
   * @example
   * ```typescript
   * const newCrop = await adjacentCropService.createFromAudit({
   *   audit_id: 123,
   *   species: 'uuid-species',
   *   variety: 'uuid-variety',
   *   is_producer_owner: true,
   *   comment: 'Detected during audit',
   *   estimated_surface: 0.5,
   *   geom: { type: 'Point', coordinates: [-70.6693, -33.4489] }
   * });
   * ```
   */
  async createFromAudit(
    data: AdjacentCropFromAuditFormValues
  ): Promise<AdjacentCropDetail> {
    try {
      const response = await apiClient.post<AdjacentCropDetail>(
        '/audit-adjacent-crops/create-from-audit/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.create_from_audit_failed');
    }
  }

  /**
   * Create a new review for an existing adjacent crop during an audit
   * 
   * @param data - The review data to create
   * @returns Promise with the created review
   * 
   * @example
   * ```typescript
   * const newReview = await adjacentCropService.createReviewFromAudit({
   *   audit_id: 456,
   *   adjacent_crop_id: 'uuid-123',
   *   comment: 'Follow-up inspection',
   *   estimated_surface: 0.6,
   *   species: 'uuid-species',
   *   variety: 'uuid-variety'
   * });
   * ```
   */
  async createReviewFromAudit(
    data: AdjacentCropReviewFormValues
  ): Promise<AdjacentCropReview> {
    try {
      const response = await apiClient.post<AdjacentCropReview>(
        '/audit-adjacent-crops/create-review/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.adjacent_crop.review_create_failed');
    }
  }

  /**
   * Get all crop species
   * 
   * @param search - Optional search term
   * @returns Promise with array of crop species
   * 
   * @example
   * ```typescript
   * const species = await adjacentCropService.getCropSpecies('tomate');
   * ```
   */
  async getCropSpecies(search?: string): Promise<CropSpecies[]> {
    try {
      const url = search 
        ? `/crop-species/?search=${encodeURIComponent(search)}`
        : '/crop-species/';
      
      const response = await apiClient.get<any>(url);
      
      // Handle paginated response
      if (response.data && response.data.results) {
        return response.data.results;
      }
      
      // Handle direct array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_species.fetch_failed');
    }
  }

  /**
   * Get a single crop species by ID
   * 
   * @param id - The ID of the crop species
   * @returns Promise with the crop species
   */
  async getCropSpeciesById(id: string): Promise<CropSpecies> {
    try {
      const response = await apiClient.get<CropSpecies>(
        `/crop-species/${id}/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_species.fetch_failed');
    }
  }

  /**
   * Create a new crop species
   * 
   * @param data - The crop species data
   * @returns Promise with the created crop species
   */
  async createCropSpecies(data: Partial<CropSpecies>): Promise<CropSpecies> {
    try {
      const response = await apiClient.post<CropSpecies>(
        '/crop-species/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_species.create_failed');
    }
  }

  /**
   * Update a crop species
   * 
   * @param id - The ID of the crop species to update
   * @param data - The partial crop species data to update
   * @returns Promise with the updated crop species
   */
  async updateCropSpecies(
    id: string,
    data: Partial<CropSpecies>
  ): Promise<CropSpecies> {
    try {
      const response = await apiClient.patch<CropSpecies>(
        `/crop-species/${id}/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_species.update_failed');
    }
  }

  /**
   * Delete a crop species
   * 
   * @param id - The ID of the crop species to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteCropSpecies(id: string): Promise<void> {
    try {
      await apiClient.delete(`/crop-species/${id}/`);
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_species.delete_failed');
    }
  }

  /**
   * Get all varieties for a specific species
   * 
   * @param speciesId - The ID of the species
   * @returns Promise with species varieties response
   * 
   * @example
   * ```typescript
   * const varietiesData = await adjacentCropService.getVarietiesBySpecies('uuid-species');
   * ```
   */
  async getVarietiesBySpecies(speciesId: string): Promise<SpeciesVarietiesResponse> {
    try {
      const response = await apiClient.get<SpeciesVarietiesResponse>(
        `/crop-species/${speciesId}/varieties/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_varieties.fetch_failed');
    }
  }

  /**
   * Get all crop varieties
   * 
   * @param filters - Optional filters (species, search)
   * @returns Promise with array of crop varieties
   * 
   * @example
   * ```typescript
   * const varieties = await adjacentCropService.getCropVarieties({
   *   species: 'uuid-species',
   *   search: 'cherry'
   * });
   * ```
   */
  async getCropVarieties(filters?: {
    species?: string;
    search?: string;
  }): Promise<CropVariety[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
      }

      const queryString = params.toString();
      const url = queryString 
        ? `/crop-varieties/?${queryString}`
        : '/crop-varieties/';
      
      const response = await apiClient.get<any>(url);
      
      // Handle paginated response
      if (response.data && response.data.results) {
        return response.data.results;
      }
      
      // Handle direct array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_varieties.fetch_failed');
    }
  }

  /**
   * Get a single crop variety by ID
   * 
   * @param id - The ID of the crop variety
   * @returns Promise with the crop variety
   */
  async getCropVarietyById(id: string): Promise<CropVariety> {
    try {
      const response = await apiClient.get<CropVariety>(
        `/crop-varieties/${id}/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_varieties.fetch_failed');
    }
  }

  /**
   * Create a new crop variety
   * 
   * @param data - The crop variety data
   * @returns Promise with the created crop variety
   */
  async createCropVariety(data: Partial<CropVariety>): Promise<CropVariety> {
    try {
      const response = await apiClient.post<CropVariety>(
        '/crop-varieties/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_varieties.create_failed');
    }
  }

  /**
   * Update a crop variety
   * 
   * @param id - The ID of the crop variety to update
   * @param data - The partial crop variety data to update
   * @returns Promise with the updated crop variety
   */
  async updateCropVariety(
    id: string,
    data: Partial<CropVariety>
  ): Promise<CropVariety> {
    try {
      const response = await apiClient.patch<CropVariety>(
        `/crop-varieties/${id}/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_varieties.update_failed');
    }
  }

  /**
   * Delete a crop variety
   * 
   * @param id - The ID of the crop variety to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteCropVariety(id: string): Promise<void> {
    try {
      await apiClient.delete(`/crop-varieties/${id}/`);
    } catch (error: any) {
      throw this.handleError(error, 'error.crop_varieties.delete_failed');
    }
  }

  /**
   * Handle API errors and provide translated error messages
   * 
   * @param error - The error object from the API
   * @param defaultMessageKey - Default translation key for the error message
   * @returns Formatted error object
   */
  private handleError(error: any, defaultMessageKey: string): ApiError {
    // If error already has a message property (from apiClient interceptor)
    if (error.message) {
      return {
        message: error.message,
        status: error.status,
        originalError: error.originalError,
      };
    }

    // Handle axios error response
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Extract error message from various possible formats
      let message = defaultMessageKey;
      
      if (typeof errorData === 'string') {
        message = errorData;
      } else if (errorData.error) {
        message = errorData.error;
      } else if (errorData.detail) {
        message = errorData.detail;
      } else if (errorData.message) {
        message = errorData.message;
      } else if (errorData.errors) {
        // Handle validation errors
        if (Array.isArray(errorData.errors)) {
          message = errorData.errors.join(', ');
        } else if (typeof errorData.errors === 'object') {
          message = Object.values(errorData.errors).flat().join(', ');
        }
      }

      return {
        message,
        status: error.response.status,
        originalError: error,
      };
    }

    // Handle network or other errors
    return {
      message: error.message || defaultMessageKey,
      originalError: error,
    };
  }
}

/**
 * Singleton instance of AdjacentCropService
 */
export const adjacentCropService = new AdjacentCropService();

export default adjacentCropService;
